import {DownOutlined} from "@ant-design/icons";
import {Button, Checkbox, message, Popover, Select, Spin, Tag, Tooltip} from "antd";
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import _ from "lodash";
import {SbomComponentServiceContext} from "../../Contexts";
import QueryOptions from "../../sal-ui/QueryOptions";
import {FindingSeverity} from "../../domain/FindingSeverity";
import {FindingStatus, formatFindingStatus} from "../../domain/FindingStatus";
import SbomComponentFinding from "../../domain/SbomComponentFinding";
import {SbomComponentFindingOption, SbomComponentFindingSort} from "../../service/SbomComponentService";
import {formatDateTime} from "../../utils/FormatUtils";
import * as findingStatusStyles from "./FindingStatus.module.css";
import * as styles from "./SbomProjectVersionDetail.module.css";
import * as sealScoreStyles from "../vulsourceitem/SealScore.module.css";

const focusedFindingSelectListHeight = 520;
const findingOptionLoadMoreThresholdPx = 24;
const focusedFindingSeverityFilterOptions = [
    FindingSeverity.CRITICAL,
    FindingSeverity.HIGH,
    FindingSeverity.MEDIUM,
    FindingSeverity.LOW,
    FindingSeverity.NONE,
    FindingSeverity.UNKNOWN
];
const focusedFindingStatusFilterOptions = [
    FindingStatus.UNKNOWN,
    FindingStatus.AFFECTED,
    FindingStatus.UNDER_INVESTIGATION,
    FindingStatus.NOT_AFFECTED
];
const focusedFindingSortOptions = [
    {value: SbomComponentFindingSort.MOST_IMPORTANT, label: "Important first"},
    {value: SbomComponentFindingSort.LEAST_IMPORTANT, label: "Least important first"},
    {value: SbomComponentFindingSort.NEWEST, label: "Newest first"},
    {value: SbomComponentFindingSort.OLDEST, label: "Oldest first"}
];

export interface ComponentFindingsPage {
    total: number;
    data: SbomComponentFinding[];
    skip: number;
    top: number;
    sort: SbomComponentFindingSort;
}

interface ComponentFindingOptionsPage {
    total: number;
    data: SbomComponentFindingOption[];
    skip: number;
    top: number;
    search: string;
    severities: FindingSeverity[];
    statuses: FindingStatus[];
    sort: SbomComponentFindingSort;
}

interface FocusedFindingOptionFilters {
    severities: FindingSeverity[];
    statuses: FindingStatus[];
    sort: SbomComponentFindingSort;
}

interface Props {
    componentId: string;
    componentFindingsPageSize: number;
    findingOptionPageSize: number;
    finding?: SbomComponentFinding;
    findings?: SbomComponentFinding[];
    findingsPage?: ComponentFindingsPage;
    findingPosition: number;
    reloadComponentFindings: (componentId: string, skip?: number, sort?: SbomComponentFindingSort) => Promise<ComponentFindingsPage | undefined>;
    onSelectFinding: (findingId: string) => void;
}

export default function FocusedFindingSelect({
    componentId,
    componentFindingsPageSize,
    findingOptionPageSize,
    finding,
    findings,
    findingsPage,
    findingPosition,
    reloadComponentFindings,
    onSelectFinding
}: Props) {
    const sbomComponentService = useContext(SbomComponentServiceContext);
    const [findingOptions, setFindingOptions] = useState<ComponentFindingOptionsPage>();
    const [findingOptionsLoading, setFindingOptionsLoading] = useState(false);
    const [severityFilter, setSeverityFilter] = useState<FindingSeverity[]>(focusedFindingSeverityFilterOptions);
    const [statusFilter, setStatusFilter] = useState<FindingStatus[]>(focusedFindingStatusFilterOptions);
    const [sort, setSort] = useState(SbomComponentFindingSort.MOST_IMPORTANT);
    const [open, setOpen] = useState(false);
    const [filterPopover, setFilterPopover] = useState<"severity" | "status">();
    const requestIdRef = useRef(0);

    const reloadFindingOptions = useCallback((selectedComponentId: string,
                                              search: string,
                                              skip: number,
                                              append: boolean,
                                              filters: FocusedFindingOptionFilters) => {
        const queryOptions = new QueryOptions("", findingOptionPageSize, skip);
        const requestId = requestIdRef.current + 1;

        requestIdRef.current = requestId;
        setFindingOptionsLoading(true);

        if (filters.severities.length === 0 || filters.statuses.length === 0) {
            setFindingOptions({
                total: 0,
                data: [],
                skip,
                top: findingOptionPageSize,
                search,
                severities: filters.severities,
                statuses: filters.statuses,
                sort: filters.sort
            });
            setFindingOptionsLoading(false);

            return Promise.resolve();
        }

        return sbomComponentService.getFindingOptions(selectedComponentId, queryOptions, search, filters)
            .then(options => {
                if (requestIdRef.current !== requestId) {
                    return;
                }

                setFindingOptions(prevPage => ({
                    total: options.total,
                    data: append && prevPage?.search === search && findingOptionFiltersEqual(prevPage, filters)
                        ? [...prevPage.data, ...options.data]
                        : options.data,
                    skip,
                    top: findingOptionPageSize,
                    search,
                    severities: filters.severities,
                    statuses: filters.statuses,
                    sort: filters.sort
                }));
            })
            .catch(() => {
                if (requestIdRef.current !== requestId) {
                    return undefined;
                }

                message.error("Cannot retrieve SBOM component finding options.");

                return undefined;
            })
            .finally(() => {
                if (requestIdRef.current === requestId) {
                    setFindingOptionsLoading(false);
                }
            });
    }, [findingOptionPageSize, sbomComponentService]);

    const debouncedReloadFindingOptions = useMemo(() => _.debounce(reloadFindingOptions, 300), [reloadFindingOptions]);

    useEffect(() => {
        return () => debouncedReloadFindingOptions.cancel();
    }, [debouncedReloadFindingOptions]);

    useEffect(() => {
        debouncedReloadFindingOptions.cancel();
        requestIdRef.current += 1;
        setFindingOptions(undefined);
        setFindingOptionsLoading(false);
    }, [componentId, debouncedReloadFindingOptions]);

    useEffect(() => {
        if (!open) {
            return;
        }

        reloadFindingOptions(componentId, "", 0, false, currentFilters());
    }, [open, componentId, severityFilter, statusFilter, sort, reloadFindingOptions]);

    function searchFindingOptions(search: string) {
        debouncedReloadFindingOptions(componentId, search, 0, false, currentFilters());
    }

    function onPopupScroll(event: React.UIEvent<HTMLDivElement>) {
        if (findingOptions === undefined || findingOptionsLoading) {
            return;
        }

        if (findingOptions.data.length >= findingOptions.total) {
            return;
        }

        const target = event.currentTarget;
        const bottomDistance = target.scrollHeight - target.scrollTop - target.clientHeight;

        if (bottomDistance <= findingOptionLoadMoreThresholdPx) {
            reloadFindingOptions(componentId, findingOptions.search, findingOptions.skip + findingOptions.top, true, {
                severities: findingOptions.severities,
                statuses: findingOptions.statuses,
                sort: findingOptions.sort
            });
        }
    }

    function selectFindingOption(findingId: string) {
        const selectedSort = findingOptions?.sort ?? sort;

        if (findingsPage?.sort === selectedSort && findingsPage.data.some(finding => finding.id === findingId)) {
            onSelectFinding(findingId);

            return;
        }

        const selectedOption = findingOptions?.data.find(option => option.id === findingId);

        if (selectedOption === undefined) {
            onSelectFinding(findingId);

            return;
        }

        const skip = Math.floor(selectedOption.position / componentFindingsPageSize) * componentFindingsPageSize;

        reloadComponentFindings(componentId, skip, selectedSort)
            .then(() => onSelectFinding(findingId));
    }

    function renderPopup(menu: React.ReactNode) {
        const hasOptions = (findingOptions?.data.length ?? 0) > 0;

        return <div className={styles['finding-focus-finding-select-popup']}>
            {renderFilters()}
            {menu}
            {findingOptionsLoading && hasOptions && renderLoadingIndicator()}
        </div>;
    }

    function renderFilters() {
        const filtersActive = severityFilter.length < focusedFindingSeverityFilterOptions.length
            || statusFilter.length < focusedFindingStatusFilterOptions.length;

        return <div className={styles['finding-focus-finding-select-filters']}
                    onMouseDown={event => event.stopPropagation()}
                    onClick={event => event.stopPropagation()}>
            {renderFilterPopover(
                "severity",
                "Severity",
                formatFilter(severityFilter, focusedFindingSeverityFilterOptions, severity => severity),
                renderSeverityFilterContent()
            )}
            {renderFilterPopover(
                "status",
                "Status",
                formatFilter(statusFilter, focusedFindingStatusFilterOptions, formatFindingStatus),
                renderStatusFilterContent()
            )}
            {filtersActive && <Button size={"small"} type={"link"} onClick={clearFilters}>Reset</Button>}
            <div className={styles['finding-focus-sort-control']}>
                <span className={styles['finding-focus-sort-label']}>Sort order</span>
                <Select<SbomComponentFindingSort>
                    size={"small"}
                    variant={"filled"}
                    className={styles['finding-focus-sort-select']}
                    value={sort}
                    options={focusedFindingSortOptions}
                    popupMatchSelectWidth={false}
                    getPopupContainer={trigger => trigger.parentElement ?? document.body}
                    onMouseDown={event => event.stopPropagation()}
                    onClick={event => event.stopPropagation()}
                    onChange={setSort}
                />
            </div>
        </div>;
    }

    function renderFilterPopover(key: "severity" | "status", label: string, value: string, content: React.ReactNode) {
        return <Popover trigger={"click"}
                        placement={"bottomLeft"}
                        destroyOnHidden={true}
                        open={filterPopover === key}
                        onOpenChange={open => setFilterPopover(open ? key : undefined)}
                        getPopupContainer={trigger => trigger.parentElement ?? document.body}
                        content={content}>
            <Button size={"small"}
                    className={styles['finding-focus-filter-button']}
                    title={`${label}: ${value}`}
                    onMouseDown={event => event.stopPropagation()}
                    onClick={event => event.stopPropagation()}>
                <span className={styles['finding-focus-filter-button-label']}>{label}:</span>
                <span className={styles['finding-focus-filter-button-value']}>{value}</span>
                <DownOutlined/>
            </Button>
        </Popover>;
    }

    function renderSeverityFilterContent() {
        return <div className={styles['finding-focus-filter-popover']}
                    onMouseDown={event => event.stopPropagation()}
                    onClick={event => event.stopPropagation()}>
            <div className={styles['finding-focus-filter-popover-actions']}>
                <Button size={"small"} type={"link"} onClick={() => setSeverityFilter(focusedFindingSeverityFilterOptions)}>
                    Select all
                </Button>
                <Button size={"small"} type={"link"} onClick={() => setSeverityFilter([])}>
                    Deselect all
                </Button>
            </div>
            <Checkbox.Group value={severityFilter}
                            className={styles['finding-focus-filter-menu']}
                            onChange={values => setSeverityFilter(orderFilter(values as FindingSeverity[], focusedFindingSeverityFilterOptions))}>
                {focusedFindingSeverityFilterOptions.map(severity => <Checkbox key={severity} value={severity}>
                    <Tag variant={"filled"}
                         onMouseDown={keepFilterOpen}
                         onClick={event => toggleSeverityFilter(event, severity)}
                         className={filterTagClassName(
                             'finding-focus-filter-severity-' + severity,
                             severityFilter.includes(severity)
                         )}>
                        {severity}
                    </Tag>
                </Checkbox>)}
            </Checkbox.Group>
            <div className={styles['finding-focus-filter-popover-footer']}>
                <Button size={"small"} onClick={() => setFilterPopover(undefined)}>Close</Button>
            </div>
        </div>;
    }

    function renderStatusFilterContent() {
        return <div className={styles['finding-focus-filter-popover']}
                    onMouseDown={event => event.stopPropagation()}
                    onClick={event => event.stopPropagation()}>
            <div className={styles['finding-focus-filter-popover-actions']}>
                <Button size={"small"} type={"link"} onClick={() => setStatusFilter(focusedFindingStatusFilterOptions)}>
                    Select all
                </Button>
                <Button size={"small"} type={"link"} onClick={() => setStatusFilter([])}>
                    Deselect all
                </Button>
            </div>
            <Checkbox.Group value={statusFilter}
                            className={styles['finding-focus-filter-menu']}
                            onChange={values => setStatusFilter(orderFilter(values as FindingStatus[], focusedFindingStatusFilterOptions))}>
                {focusedFindingStatusFilterOptions.map(status => <Checkbox key={status} value={status}>
                    <Tag variant={"filled"}
                         onMouseDown={keepFilterOpen}
                         onClick={event => toggleStatusFilter(event, status)}
                         className={filterTagClassName(
                             'finding-focus-filter-status-' + status,
                             statusFilter.includes(status)
                         )}>
                        {formatFindingStatus(status)}
                    </Tag>
                </Checkbox>)}
            </Checkbox.Group>
            <div className={styles['finding-focus-filter-popover-footer']}>
                <Button size={"small"} onClick={() => setFilterPopover(undefined)}>Close</Button>
            </div>
        </div>;
    }

    function renderOption(findingOption?: SbomComponentFindingOption) {
        if (findingOption === undefined) {
            return null;
        }

        const title = findingOption.label;

        return <div className={styles['finding-focus-finding-option']}>
            {renderCveIds(findingOption.cveIds)}
            <div className={styles['finding-focus-finding-option-title']} title={title}>
                <span title={findingOption.severity ? `Severity: ${findingOption.severity}` : undefined}
                      className={[
                          styles['finding-focus-finding-option-severity'],
                          findingOption.severity
                              ? sealScoreStyles['severity-' + findingOption.severity]
                              : styles['finding-focus-finding-option-severity-empty']
                      ].filter(Boolean).join(" ")}/>
                <span className={styles['finding-focus-finding-option-title-text']}>{title}</span>
            </div>
            <div className={styles['finding-focus-finding-option-meta']}>
                {findingOption.sourcePublished && <span>{formatDateTime(findingOption.sourcePublished)}</span>}
                {findingOption.status && <Tag variant={"filled"} className={findingStatusStyles['status-' + findingOption.status]}>
                    {formatFindingStatus(findingOption.status)}
                </Tag>}
            </div>
        </div>;
    }

    function renderCveIds(cveIds: string[] = []) {
        if (cveIds.length === 0) {
            return <span className={styles['finding-focus-finding-option-cves-empty']}/>;
        }

        const rest = cveIds.slice(1);

        return <div className={styles['finding-focus-finding-option-cves']}>
            <Tag variant={"filled"} color={"blue"} className={styles['finding-focus-finding-option-cve']} title={cveIds[0]}>
                {cveIds[0]}
            </Tag>
            {rest.length > 0 && <Tooltip title={renderCveTooltip(rest)} overlayStyle={{maxWidth: "none"}}>
                <Tag variant={"filled"} color={"blue"} className={styles['finding-focus-finding-option-cve-more']}>+{rest.length}</Tag>
            </Tooltip>}
        </div>;
    }

    function renderCveTooltip(cveIds: string[]) {
        return <div>
            {cveIds.map(cveId => <div key={cveId}>{cveId}</div>)}
        </div>;
    }

    function renderLoadingIndicator() {
        return <div className={styles['finding-focus-finding-select-loading']}>
            <Spin size={"small"}/>
            <span>Loading findings...</span>
        </div>;
    }

    function currentFilters() {
        return {
            severities: severityFilter,
            statuses: statusFilter,
            sort
        };
    }

    function getFindingOptions() {
        const options = findingOptions?.data ?? [];

        if (finding === undefined || options.some(option => option.id === finding.id)) {
            return options;
        }

        return [optionFromFinding(finding, findingPosition), ...options];
    }

    function optionFromFinding(finding: SbomComponentFinding, position: number): SbomComponentFindingOption {
        return {
            id: finding.id,
            label: formatFindingSelectLabel(finding),
            severity: finding.severity,
            status: finding.status,
            sourcePublished: finding.vulSourceItem?.sourcePublished,
            cveIds: readFindingCveIds(finding),
            position
        };
    }

    function clearFilters() {
        setSeverityFilter(focusedFindingSeverityFilterOptions);
        setStatusFilter(focusedFindingStatusFilterOptions);
    }

    function keepFilterOpen(event: React.MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
    }

    function toggleSeverityFilter(event: React.MouseEvent, severity: FindingSeverity) {
        event.preventDefault();
        event.stopPropagation();

        setSeverityFilter(values => orderFilter(toggleFilterValue(values, severity), focusedFindingSeverityFilterOptions));
    }

    function toggleStatusFilter(event: React.MouseEvent, status: FindingStatus) {
        event.preventDefault();
        event.stopPropagation();

        setStatusFilter(values => orderFilter(toggleFilterValue(values, status), focusedFindingStatusFilterOptions));
    }

    function filterTagClassName(colorClassName: string, selected: boolean) {
        return [
            styles['finding-focus-filter-tag'],
            selected && styles['finding-focus-filter-tag-selected'],
            !selected && styles['finding-focus-filter-tag-excluded'],
            selected && styles[colorClassName]
        ].filter(Boolean).join(" ");
    }

    function formatFilter<T>(values: T[], allValues: T[], formatValue: (value: T) => string | undefined) {
        if (values.length === allValues.length) {
            return "All";
        }

        if (values.length === 0) {
            return "None";
        }

        return values.map(formatValue).filter(Boolean).join(", ");
    }

    function toggleFilterValue<T>(values: T[], value: T) {
        return values.includes(value)
            ? values.filter(item => item !== value)
            : [...values, value];
    }

    function orderFilter<T>(values: T[], orderedValues: T[]) {
        return orderedValues.filter(value => values.includes(value));
    }

    function findingOptionFiltersEqual(page: ComponentFindingOptionsPage, filters: FocusedFindingOptionFilters) {
        return _.isEqual(page.severities, filters.severities)
            && _.isEqual(page.statuses, filters.statuses)
            && page.sort === filters.sort;
    }

    function formatFindingSelectLabel(finding: SbomComponentFinding) {
        return finding.vulSourceItem?.attributes?.title
            || finding.vulSourceItem?.attributes?.description
            || finding.vulSourceItem?.id
            || finding.id
            || "";
    }

    function readFindingCveIds(finding: SbomComponentFinding) {
        const attributes = finding.vulSourceItem?.attributes ?? {};

        return uniqueTexts(attributeTextValues(attributes.cveId));
    }

    function attributeTextValues(value: unknown) {
        if (Array.isArray(value)) {
            return value.filter((item): item is string => typeof item === "string" && item.trim() !== "");
        }

        return typeof value === "string" && value.trim() !== "" ? [value] : [];
    }

    function uniqueTexts(values: string[]) {
        return [...new Set(values)];
    }

    const options = getFindingOptions();

    return <Select
        className={styles['finding-focus-finding-select']}
        classNames={{popup: {listItem: styles['finding-focus-finding-select-list-item']}}}
        styles={{popup: {list: {maxHeight: focusedFindingSelectListHeight}}}}
        size={"small"}
        variant={"borderless"}
        value={finding?.id}
        open={open}
        popupMatchSelectWidth={false}
        listHeight={focusedFindingSelectListHeight}
        virtual={false}
        showSearch={true}
        loading={findings === undefined || findingOptionsLoading}
        disabled={findings === undefined || findings.length === 0}
        placeholder={findings === undefined ? "Loading findings..." : undefined}
        filterOption={false}
        popupRender={renderPopup}
        notFoundContent={findingOptionsLoading ? renderLoadingIndicator() : undefined}
        optionRender={option => renderOption(option.data.findingOption)}
        onOpenChange={setOpen}
        onSearch={searchFindingOptions}
        onPopupScroll={onPopupScroll}
        onChange={findingId => {
            selectFindingOption(findingId);
            setOpen(false);
        }}
        options={options.map(findingOption => ({
            value: findingOption.id,
            label: findingOption.label,
            findingOption
        }))}
    />;
}
